import type {
  PartnerShopPlannerContent,
  PartnerShopPlannerContentPhoto,
  PartnerShopPlannerPhoto,
} from '@/api/partnerShopDetail';

interface PlannerContentSectionProps {
  content: PartnerShopPlannerContent;
  photos?: PartnerShopPlannerPhoto[];
}

function buildPhotoMap(photos: PartnerShopPlannerPhoto[]) {
  return new Map(photos.map((photo) => [photo.photoKey, photo]));
}

function getPhotoAlt(photo: PartnerShopPlannerPhoto, fallback?: string) {
  return fallback || photo.imageSummary || photo.imageCategoryName || '플래너 큐레이션 사진';
}

function isPlannerPhoto(value: PartnerShopPlannerContentPhoto): value is PartnerShopPlannerPhoto {
  return 'imageUrl' in value && Boolean(value.imageUrl);
}

function PlannerPhotoFigure({
  photo,
  alt,
}: {
  photo: PartnerShopPlannerPhoto;
  alt?: string;
}) {
  return (
    <figure className="m-0 aspect-[4/3] overflow-hidden rounded-[12px] bg-[#F4F4F5] md:rounded-2xl">
      <img
        src={photo.imageUrl}
        alt={getPhotoAlt(photo, alt)}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="block h-full w-full object-cover"
      />
    </figure>
  );
}

function PhotoRefGrid({
  photoItems,
  photoMap,
}: {
  photoItems: PartnerShopPlannerContentPhoto[];
  photoMap: Map<string, PartnerShopPlannerPhoto>;
}) {
  const resolvedPhotos = photoItems
    .map((ref) => {
      const photo = isPlannerPhoto(ref) ? ref : photoMap.get(ref.photoKey);
      if (!photo) return null;

      return {
        photo,
        caption: ref.caption,
        alt: ref.alt,
      };
    })
    .filter((item): item is { photo: PartnerShopPlannerPhoto; caption?: string; alt?: string } => Boolean(item));

  if (resolvedPhotos.length === 0) return null;

  return (
    <div className="my-5 grid grid-cols-2 gap-2 md:my-6 md:gap-3">
      {resolvedPhotos.map(({ photo, caption, alt }) => (
        <PlannerPhotoFigure
          key={photo.photoKey}
          photo={photo}
          alt={alt || caption}
        />
      ))}
    </div>
  );
}

function PhotoKeyGrid({
  photos,
  photoKeys,
  photoMap,
}: {
  photos?: PartnerShopPlannerPhoto[];
  photoKeys?: string[];
  photoMap: Map<string, PartnerShopPlannerPhoto>;
}) {
  const photoItems: PartnerShopPlannerContentPhoto[] = photos?.length
    ? photos
    : (photoKeys || []).map((photoKey) => ({ photoKey }));

  if (photoItems.length === 0) return null;

  return (
    <PhotoRefGrid
      photoMap={photoMap}
      photoItems={photoItems}
    />
  );
}

export default function PlannerContentSection({ content, photos }: PlannerContentSectionProps) {
  const photoMap = buildPhotoMap(photos || []);
  const sections = content.sections || [];
  const heroPhotos = content.heroPhotos || [];
  const hasHeroPhotos = heroPhotos.length > 0;
  const hasSectionPhotos = sections.some((section) => {
    return Boolean(section.photos?.length || section.photoKeys?.length);
  });

  if (!content || !hasHeroPhotos && !hasSectionPhotos && sections.length === 0) {
    return null;
  }

  return (
    <section className="bg-white md:rounded-2xl md:p-0 lg:p-0">
      <div>
        <span className="mb-3.5 inline-flex items-center gap-[5px] rounded-full bg-[#17171A] px-[11px] py-[5px] text-[11px] font-semibold text-white md:mb-4 md:gap-1.5 md:px-3 md:text-[12px]">
          <span className="h-1 w-1 rounded-full bg-[#D6927A] md:h-[5px] md:w-[5px]" />
          다이렉트웨딩 후기 기반 검증 업체
        </span>

        <h2 className="m-0 mb-3 text-[21px] font-bold leading-[1.4] tracking-[-0.0194em] text-[#17171A] md:mb-4 md:text-[26px] md:tracking-[-0.023em]">
          {content.headline}
        </h2>

        {content.introduction && (
          <p className="m-0 mb-4 text-[14.5px] leading-[1.8] text-[rgba(46,47,51,0.88)] md:mb-3 md:text-[15.5px]">
            {content.introduction}
          </p>
        )}

        <PhotoRefGrid photoItems={heroPhotos} photoMap={photoMap} />

        {sections.map((section, index) => (
          <div key={`${section.title}-${index}`}>
            <section>
              <h3 className="m-0 mb-2 text-[17px] font-bold tracking-[-0.012em] text-[#17171A] md:mb-2.5 md:text-[19px]">
                {section.title}
              </h3>
              <p className="m-0 mb-5 text-[14.5px] leading-[1.8] text-[rgba(46,47,51,0.88)] md:mb-6 md:text-[15.5px]">
                {section.body}
              </p>
              <PhotoKeyGrid
                photos={section.photos}
                photoKeys={section.photoKeys}
                photoMap={photoMap}
              />
            </section>
          </div>
        ))}

        {content.plannerQuote?.text && (
          <blockquote className="m-0 mt-6 rounded-xl bg-[#FBF1ED] px-[18px] py-4 md:mt-6 md:px-6 md:py-5">
            <p className="m-0 font-serif text-[14.5px] font-semibold leading-[1.9] tracking-[-0.02em] text-[#17171A] md:text-[16px]">
              &quot;{content.plannerQuote.text}&quot;
            </p>
            <footer className="mt-1.5 text-[11.5px] text-[rgba(55,56,60,0.4)] md:text-[12px]">
              다이렉트 카페 실제 후기 중에서
            </footer>
          </blockquote>
        )}
      </div>
    </section>
  );
}
